import type { CartState } from "@ai-guidance/shared";
import { formatMoney } from "./shopify";
import { commerceText, type CommerceCopy } from "./text";
import type { AdapterResult, CommerceAdapter, CommerceConfig, PriceBounds, SearchResults } from "./types";

/**
 * Adaptorul WooCommerce: Store API, rutele publice ale oricărui magazin Woo
 * (developer.woocommerce.com → Store API):
 *   GET  /wp-json/wc/store/v1/cart              → coșul întreg + header `Nonce`
 *   POST /wp-json/wc/store/v1/cart/add-item     { id, quantity }
 *   POST /wp-json/wc/store/v1/cart/update-item  { key, quantity }
 *   POST /wp-json/wc/store/v1/cart/remove-item  { key }
 *
 * Același principiu ca la Shopify (docs/plan-commerce-actions.md §1): API
 * oficial, nu click-uri în DOM. E chiar API-ul pe care îl cheamă blocurile de
 * coș ale Woo din browserul oricărui vizitator, deci nu depinde de temă și nu
 * reîncarcă pagina - vocea nu se întrerupe.
 *
 * DOUĂ diferențe de mecanică față de Shopify, ambele vizibile în codul de mai jos:
 *
 * 1. **Nonce.** Mutațiile cer headerul `Nonce`, pe care Store API îl întoarce la
 *    fiecare răspuns (inclusiv la cele de eroare). Îl ținem de la ultimul
 *    răspuns văzut; dacă n-avem niciunul, facem întâi un GET pe coș. Expiră
 *    (403 `woocommerce_rest_invalid_nonce`) - și atunci chiar răspunsul de 403
 *    poartă unul proaspăt, deci reîncercăm O SINGURĂ dată.
 * 2. **Cheia de linie.** `add-item` lucrează cu id-ul produsului, dar
 *    `update-item`/`remove-item` NU: ele cer `key`, cheia liniei din coș. Deci
 *    orice schimbare de cantitate începe cu o citire a coșului, ca să traducem
 *    variantId → key. Nu e o optimizare ratată, e contractul platformei.
 *
 * Nimic din fișierul ăsta nu aruncă: fiecare drum de eșec iese ca
 * `{ ok: false, message }`, respectiv `null` pentru `getCart`.
 */

const STORE_API = "/wp-json/wc/store/v1";

/** Cât păstrăm dintr-un mesaj de eroare al platformei (schema îl plafonează oricum la 200). */
const MAX_ERROR_CHARS = 160;

/**
 * Codurile de eroare care descriu o greșeală de-a NOASTRĂ, nu o situație pe
 * care vizitatorul o poate rezolva: nonce expirat, id inexistent, parametru
 * lipsă. Textul lor („Nonce-ul nu este valid") nu spune nimic nimănui în afară
 * de noi și arată ca un sit stricat - deci cade pe textul nostru din `text.ts`,
 * iar cauza rămâne în consolă.
 *
 * Tot ce NU e în listă păstrează mesajul platformei: erorile de STOC („Nu mai
 * avem decât 2 bucăți din X") sunt deja în limba magazinului și mai exacte
 * decât orice am scrie noi. Aceeași împărțire ca la Shopify, doar că acolo se
 * face pe forma textului (Woo ne dă un cod, ceea ce e mai sigur).
 */
const TECHNICAL_ERROR_CODES = [
  "woocommerce_rest_invalid_nonce",
  "woocommerce_rest_missing_nonce",
  "woocommerce_rest_cart_invalid_product",
  "woocommerce_rest_cart_invalid_key",
  "woocommerce_rest_cart_item_error",
  "rest_invalid_param",
  "rest_missing_callback_param",
  "rest_no_route",
];

interface WooError {
  code?: unknown;
  message?: unknown;
}

interface WooCartItem {
  key?: unknown;
  id?: unknown;
  name?: unknown;
  quantity?: unknown;
  totals?: { line_total?: unknown; line_total_tax?: unknown } | null;
}

interface WooCart {
  items?: unknown;
  items_count?: unknown;
  totals?: {
    total_items?: unknown;
    total_items_tax?: unknown;
    currency_code?: unknown;
    currency_minor_unit?: unknown;
  } | null;
}

/**
 * Sumele Store API vin ca ȘIRURI în subunități, cu exponentul declarat alături
 * (`currency_minor_unit`). Normalizăm la bani întregi cu 2 zecimale, forma pe
 * care o știe `formatMoney` (refolosit din shopify.ts - formatarea e a monedei,
 * nu a platformei).
 */
export function minorToCents(value: unknown, minorUnit: number): number {
  const raw = typeof value === "string" ? Number(value) : typeof value === "number" ? value : NaN;
  if (!Number.isFinite(raw)) return 0;
  return Math.round(raw * 10 ** (2 - minorUnit));
}

function asText(value: unknown, max: number): string {
  return typeof value === "string" ? value.slice(0, max) : "";
}

function asNumber(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

/**
 * Răspunsul Store API pe coș → `CartState`, tăiat la plafoanele schemei partajate.
 *
 * `totalPrice` se compune din `total_items` (+ taxa lor), NU din `total_price`:
 * acela include livrarea, iar Shopify - deci și tot ce citește agentul azi -
 * vorbește despre valoarea PRODUSELOR din coș. Un total care sare cu 35 de lei
 * fiindcă s-a aplicat o taxă de transport ar face agentul să spună un preț pe
 * care vizitatorul nu-l vede pe cardurile lui.
 *
 * Taxa se adună fiindcă magazinele care afișează prețuri fără TVA o țin separat;
 * pe unul care afișează cu TVA inclus (cazul magazinului nostru de test)
 * `*_tax` e 0, deci adunarea nu schimbă nimic.
 */
export function toCartState(raw: unknown): CartState | null {
  if (!raw || typeof raw !== "object") return null;
  const cart = raw as WooCart;
  // Un magazin real întoarce mereu `items_count`; un sit care nu e WooCommerce
  // (sau o rută care servește HTML) nu - și atunci nu inventăm un coș gol.
  if (typeof cart.items_count !== "number") return null;
  const totals = cart.totals ?? {};
  const minorUnit = typeof totals.currency_minor_unit === "number" ? totals.currency_minor_unit : 2;
  const currency = asText(totals.currency_code, 10) || "RON";
  const rawItems = Array.isArray(cart.items) ? (cart.items as WooCartItem[]) : [];
  const items = rawItems.slice(0, 100).map((item) => {
    const lineCents = minorToCents(item.totals?.line_total, minorUnit) + minorToCents(item.totals?.line_total_tax, minorUnit);
    return {
      variantId: String(item.id ?? "").slice(0, 64),
      title: asText(item.name, 300),
      quantity: Math.min(999, Math.max(0, Math.round(asNumber(item.quantity)))),
      ...(item.totals ? { linePrice: formatMoney(lineCents, currency).slice(0, 50) } : {}),
    };
  });
  const itemsCents = minorToCents(totals.total_items, minorUnit) + minorToCents(totals.total_items_tax, minorUnit);
  return {
    items,
    itemCount: Math.min(9999, Math.max(0, Math.round(cart.items_count))),
    totalPrice: formatMoney(itemsCents, currency).slice(0, 50),
  };
}

/** Mesajul scurt al unui eșec, preferând descrierea platformei când e pentru OM. */
function errorMessage(body: unknown, copy: CommerceCopy): string {
  if (body && typeof body === "object") {
    const err = body as WooError;
    const code = typeof err.code === "string" ? err.code : "";
    const text = typeof err.message === "string" ? err.message.trim() : "";
    if (code && TECHNICAL_ERROR_CODES.includes(code)) {
      // În consolă, nu în toast: e diagnosticul nostru, nu al vizitatorului.
      console.warn("[ai-guidance] Store API rejected the request:", `${code} ${text}`.slice(0, MAX_ERROR_CHARS));
      return copy.cartFailed;
    }
    if (text) return text.slice(0, MAX_ERROR_CHARS);
  }
  return copy.cartFailed;
}

/** Numele headerului de nonce, plus varianta veche (Woo < 8.x). */
const NONCE_HEADERS = ["Nonce", "X-WC-Store-API-Nonce"];

function readNonce(headers: Headers): string | null {
  for (const name of NONCE_HEADERS) {
    const value = headers.get(name);
    if (value) return value;
  }
  return null;
}

/** Grila de produse a temei, în forma pe care WooCommerce o impune prin `wc_get_template`. */
const PRODUCT_GRID_SELECTOR = "ul.products";
const PRODUCT_ITEM_SELECTOR = "li.product";
const SEARCH_RESULTS_MAX_COUNT = 200;
const SEARCH_QUERY_MAX_CHARS = 200;

/**
 * Câte produse s-au randat pe pagina curentă, dacă e o pagină de rezultate sau o
 * arhivă de produse.
 *
 * Spre deosebire de Shopify, aici EXISTĂ un contract: `ul.products > li.product`
 * e markup-ul pe care îl scriu template-urile WooCommerce (`content-product.php`),
 * nu tema - o temă îl stilizează, dar nu-l rescrie, altfel își pierde toate
 * hook-urile platformei. Deci numărăm elemente, nu linkuri deduplicate.
 *
 * Regula de aur rămâne cea de la Shopify: un `0` MINCINOS e mai rău decât lipsa
 * semnalului. Zero se raportează doar când suntem pe o pagină de rezultate ȘI
 * pagina e chiar una WooCommerce (`body.woocommerce`, pusă de platformă); în
 * rest - `undefined`, adică „nu știu".
 */
function readSearchResults(): SearchResults | undefined {
  if (typeof document === "undefined" || typeof location === "undefined") return undefined;
  const params = new URLSearchParams(location.search);
  const body = document.body;
  if (!body) return undefined;
  const classes = body.classList;
  const isWoo = classes.contains("woocommerce") || classes.contains("woocommerce-page");
  // Rezultate de căutare pe produse, sau o arhivă/categorie de produse - în
  // ambele cazuri grila e aceeași, și în ambele agentul tocmai a predat pagina.
  const isResultsPage =
    (params.has("s") && isWoo) ||
    classes.contains("post-type-archive-product") ||
    classes.contains("tax-product_cat") ||
    classes.contains("woocommerce-shop");
  if (!isResultsPage) return undefined;

  const raw = params.get("s");
  const query = raw ? raw.trim().slice(0, SEARCH_QUERY_MAX_CHARS) : "";
  const grid = document.querySelector(PRODUCT_GRID_SELECTOR);
  // Fără grilă randată nu spunem „gol": pagina poate fi încă în construcție
  // (temă block care randează din JS), iar un zero fals declanșează recuperarea
  // pe rezultate goale peste o pagină plină.
  if (!grid) return undefined;
  const count = grid.querySelectorAll(PRODUCT_ITEM_SELECTOR).length;
  return { count: Math.min(count, SEARCH_RESULTS_MAX_COUNT), ...(query ? { query } : {}) };
}

export function createWooCommerceAdapter(
  config: CommerceConfig,
  copy: CommerceCopy = commerceText(),
): CommerceAdapter {
  /** Ultimul nonce văzut. Store API îl întoarce pe FIECARE răspuns, inclusiv pe erori. */
  let nonce: string | null = null;

  /**
   * `credentials: "same-origin"` e explicit fiindcă sesiunea de coș a Woo stă în
   * cookie-uri (`woocommerce_cart_hash`, `wp_woocommerce_session_*`): fără el,
   * fiecare cerere ar deschide un coș nou și vizitatorul n-ar vedea niciodată ce
   * i-am adăugat.
   */
  async function call(
    path: string,
    init?: { method: string; body: Record<string, unknown> },
  ): Promise<{ ok: boolean; status: number; body: unknown } | null> {
    try {
      const res = await fetch(`${STORE_API}${path}`, {
        credentials: "same-origin",
        ...(init
          ? {
              method: init.method,
              headers: {
                "content-type": "application/json",
                accept: "application/json",
                ...(nonce ? { Nonce: nonce } : {}),
              },
              body: JSON.stringify(init.body),
            }
          : { headers: { accept: "application/json" } }),
      });
      // Înaintea citirii corpului: și un 403 poartă un nonce proaspăt, care e
      // exact ce ne trebuie ca să reîncercăm.
      const fresh = readNonce(res.headers);
      if (fresh) nonce = fresh;
      return { ok: res.ok, status: res.status, body: await res.json().catch(() => null) };
    } catch {
      return null;
    }
  }

  /** Coșul brut, cu efectul secundar util: reîmprospătează nonce-ul. */
  async function rawCart(): Promise<unknown> {
    const res = await call("/cart");
    return res?.ok ? res.body : null;
  }

  /**
   * O mutație, cu o singură reîncercare pe nonce expirat.
   *
   * Reîncercarea e sigură fiindcă cererea a fost REFUZATĂ: un 403 de nonce se
   * întâmplă înainte ca Woo să atingă coșul, deci nu există riscul de a adăuga
   * de două ori. Orice alt eșec nu se reia.
   */
  async function mutate(path: string, body: Record<string, unknown>): Promise<AdapterResult> {
    if (!nonce) await rawCart();
    let res = await call(path, { method: "POST", body });
    if (res && !res.ok && res.status === 403) {
      const code = (res.body as WooError | null)?.code;
      if (code === "woocommerce_rest_invalid_nonce" || code === "woocommerce_rest_missing_nonce") {
        // `call` tocmai a citit nonce-ul proaspăt din răspunsul de 403; dacă
        // răspunsul n-a purtat niciunul, îl luăm de pe coș.
        if (!nonce) await rawCart();
        res = await call(path, { method: "POST", body });
      }
    }
    if (!res) return { ok: false, message: copy.cartFailed };
    if (!res.ok) return { ok: false, message: errorMessage(res.body, copy) };
    return { ok: true };
  }

  /** Cheia liniei din coș pentru un produs. `null` = nu e în coș acum. */
  async function lineKey(variantId: string): Promise<string | null> {
    const cart = await rawCart();
    const items = cart && typeof cart === "object" ? (cart as WooCart).items : null;
    if (!Array.isArray(items)) return null;
    for (const item of items as WooCartItem[]) {
      if (String(item?.id ?? "") === String(variantId) && typeof item?.key === "string") return item.key;
    }
    return null;
  }

  return {
    platform: "woocommerce",
    addToCart(variantId, quantity) {
      // `id` NUMERIC: Store API validează tipul, iar catalogul poartă id-ul ca
      // string. O formă care nu e din cifre trece neatinsă - platforma decide.
      const id = /^\d+$/.test(variantId) ? Number(variantId) : variantId;
      return mutate("/cart/add-item", { id, quantity });
    },
    async setQuantity(variantId, quantity) {
      const key = await lineKey(variantId);
      if (!key) {
        // Onest, nu tăcut: agentul primește textul în `lastActionError` și
        // corectează verbal la tura următoare, în loc să „confirme" o scoatere
        // care nu s-a întâmplat.
        return { ok: false, message: copy.cartLineMissing };
      }
      // Woo are rută separată pentru scoatere; `quantity: 0` pe update-item e
      // respins de validarea lui (minimul e 1).
      return quantity === 0
        ? mutate("/cart/remove-item", { key })
        : mutate("/cart/update-item", { key, quantity });
    },
    async getCart() {
      return toCartState(await rawCart());
    },
    cartPath() {
      return config.cartPath;
    },
    /**
     * Căutarea WooCommerce e căutarea WordPress, restrânsă la produse:
     * `/?s=<q>&post_type=product`. Forma cu query string, nu `/?s=...` rescris
     * de temă, fiindcă e singura care funcționează pe ORICE structură de
     * permalink-uri - inclusiv pe magazinele în altă limbă, unde slug-urile sunt
     * traduse (`/produs/`, `/categorie/`), dar `?s=` rămâne al platformei.
     *
     * `encodeURIComponent` (nu `URLSearchParams`) fiindcă vrem `%20`, nu `+`:
     * ambele merg, dar calea ajunge și în bara de adrese, sub ochii omului.
     */
    searchPath(query, bounds?: PriceBounds) {
      const params = [`s=${encodeURIComponent(query.trim())}`, "post_type=product"];
      // `min_price`/`max_price`, în monedă întreagă: parametrii widgetului de
      // filtrare după preț al WooCommerce, citiți de query-ul de produse.
      // ACELAȘI caveat ca la Shopify: îi consumă widgetul/blocul de filtrare -
      // o temă fără el îi ignoră pur și simplu, deci rezultatele rămân corecte,
      // doar nestrâmtate. Nu simulăm niciodată slider-ul temei.
      if (typeof bounds?.minPrice === "number") params.push(`min_price=${bounds.minPrice}`);
      if (typeof bounds?.maxPrice === "number") params.push(`max_price=${bounds.maxPrice}`);
      return `/?${params.join("&")}`;
    },
    searchResults() {
      return readSearchResults();
    },
  };
}
